import { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import MessageComponent from './MessageComponent';

// Collapsible message component for individual emails within a thread
export default function CollapsibleEmailMessage({
  message,
  mappedMessage,
  senderName,
  senderEmail,
  formattedTimestamp,
  previewText,
  isLastMessage,
  profile_picture_url,
  defaultPic,
  fullname,
  getOwnerDetails,
  setHoveredMessageId,
}) {
  const [isOpen, setIsOpen] = useState(isLastMessage);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn(
        'group/copilot relative mt-4 rounded-lg bg-muted/50',
        isLastMessage ? 'mb-4' : ''
      )}
    >
      <div
        onMouseEnter={() => setHoveredMessageId(message._id)}
        onMouseLeave={() => setHoveredMessageId(null)}
      >
        {/* Collapsed header - clickable to expand */}
        <CollapsibleTrigger asChild>
          <div
            className={cn(
              'flex cursor-pointer items-center gap-3 p-3',
              !isOpen && 'hover:bg-muted/80',
              isOpen && 'border-b border-border/50'
            )}
          >
            {/* Chevron indicator */}
            <ChevronRight
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                isOpen && 'rotate-90'
              )}
            />

            {/* Avatar */}
            <div className='shrink-0'>
              <Avatar className='h-8 w-8'>
                {message.direction === 'sent' ? (
                  <>
                    <AvatarImage
                      src={getOwnerDetails(message.user_id, 'image', 'email')}
                    />
                    <AvatarFallback>
                      {getOwnerDetails(message.user_id, 'initials', 'email')}
                    </AvatarFallback>
                  </>
                ) : profile_picture_url ? (
                  <AvatarImage src={profile_picture_url} />
                ) : (message.sender_name || fullname)?.[0] ? (
                  <AvatarFallback>
                    {(message.sender_name || fullname)
                      ?.charAt(0)
                      ?.toUpperCase() || ''}
                  </AvatarFallback>
                ) : (
                  <>
                    <AvatarImage src={defaultPic} />
                    <AvatarFallback>
                      {(message.sender_name || fullname)
                        ?.charAt(0)
                        ?.toUpperCase() || ''}
                    </AvatarFallback>
                  </>
                )}
              </Avatar>
            </div>

            {/* Header info */}
            <div className='min-w-0 flex-1'>
              <div className='flex items-center justify-between gap-2'>
                <div className='flex min-w-0 items-center gap-1'>
                  <span className='shrink-0 text-sm font-medium text-foreground'>
                    {senderName}
                  </span>
                  {senderEmail && (
                    <span className='min-w-0 truncate text-xs text-muted-foreground'>
                      &lt;{senderEmail}&gt;
                    </span>
                  )}
                </div>
                <span className='shrink-0 text-xs text-muted-foreground'>
                  {formattedTimestamp}
                </span>
              </div>

              {/* Preview text - only show when collapsed */}
              {!isOpen && previewText && (
                <p className='mt-0.5 truncate text-sm text-muted-foreground'>
                  {previewText}
                  {previewText.length >= 100 && '...'}
                </p>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        {/* Expanded content */}
        <CollapsibleContent>
          <div className='p-3 pt-2'>
            <div className='whitespace-pre-wrap break-words'>
              <MessageComponent message={mappedMessage} />
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
